import type {
  Sql, Fila, Parcela, Edificio, Parte, Inmueble, Candidato, BusquedaGuardada, TeselaCargada, Valor, CursoAguaFila,
} from './tipos';
import type { Bbox, Punto, MultiPoligono } from '../geo/geometria';
import { bboxMultiPoligono, puntoEnMultiPoligono } from '../geo/geometria';
import { IndiceSegmentos } from '../geo/distancias';

/** Valor de `dist_agua_m` cuando no hay ningun curso de agua a menos de la distancia medida (2 km). */
export const SIN_AGUA_CERCA_M = 9999;

/**
 * Consultas de la base de datos local. Todas reciben el motor `Sql` para poder ejecutarse
 * tanto en el worker (sqlite-wasm) como en los tests (node:sqlite).
 */

// ---------- Ingesta ----------

export function insertarParcelas(sql: Sql, filas: Parcela[]): void {
  // Al refrescar una tesela se conserva la distancia al agua ya calculada (las subconsultas se
  // evaluan antes de que REPLACE borre la fila anterior).
  const s = `INSERT OR REPLACE INTO parcelas
    (rc, etiqueta, area_m2, cx, cy, minx, miny, maxx, maxy, n_edificios, n_partes, geom, epsg, tesela, actualizada,
     dist_agua_m, agua_nombre, agua_id)
    VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?,
      COALESCE((SELECT COUNT(*) FROM edificios e WHERE e.rc = ?), 0),
      COALESCE((SELECT COUNT(*) FROM partes p WHERE p.rc = ?), 0), ?, ?, ?, ?,
      (SELECT dist_agua_m FROM parcelas x WHERE x.rc = ?), (SELECT agua_nombre FROM parcelas x WHERE x.rc = ?), (SELECT agua_id FROM parcelas x WHERE x.rc = ?))`;
  sql.transaction(() => {
    for (const p of filas) {
      sql.run(s, [p.rc, p.etiqueta, p.areaM2, p.cx, p.cy, p.minx, p.miny, p.maxx, p.maxy,
        p.rc, p.rc, p.geom, p.epsg, p.tesela, p.actualizada, p.rc, p.rc, p.rc]);
    }
  });
}

export function insertarEdificios(sql: Sql, filas: Edificio[]): void {
  const s = `INSERT OR REPLACE INTO edificios
    (rc, uso, estado, anio, plantas, unidades, viviendas, sup_construida_m2, huella_m2, foto_url, geom,
     alero_m, cumbrera_m, huella_corregida, desviacion_m)
    VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`;
  sql.transaction(() => {
    for (const e of filas) {
      sql.run(s, [e.rc, e.uso, e.estado, e.anio, e.plantas, e.unidades, e.viviendas, e.supConstruidaM2,
        e.huellaM2, e.fotoUrl, e.geom, e.aleroM, e.cumbreraM, e.huellaCorregida, e.desviacionM]);
      sql.run(`UPDATE parcelas SET n_edificios = (SELECT COUNT(*) FROM edificios x WHERE x.rc = parcelas.rc) WHERE rc = ?`, [e.rc]);
    }
  });
}

export function insertarPartes(sql: Sql, filas: Parte[]): void {
  const s = `INSERT OR REPLACE INTO partes (id, rc, plantas_sobre, plantas_bajo, altura_bajo_m, estado, huella_m2, geom)
    VALUES (?, ?, ?, ?, ?, ?, ?, ?)`;
  sql.transaction(() => {
    const rcs = new Set<string>();
    for (const p of filas) {
      sql.run(s, [p.id, p.rc, p.plantasSobre, p.plantasBajo, p.alturaBajoM, p.estado, p.huellaM2, p.geom]);
      rcs.add(p.rc);
    }
    for (const rc of rcs) {
      sql.run(`UPDATE parcelas SET n_partes = (SELECT COUNT(*) FROM partes x WHERE x.rc = parcelas.rc) WHERE rc = ?`, [rc]);
      // El edificio suele traer las plantas vacias: se completan con el maximo de sus partes.
      sql.run(`UPDATE edificios SET plantas = COALESCE(plantas, (SELECT MAX(plantas_sobre) FROM partes x WHERE x.rc = edificios.rc)) WHERE rc = ?`, [rc]);
    }
  });
}

export function reemplazarInmuebles(sql: Sql, rc: string, filas: Inmueble[]): void {
  sql.transaction(() => {
    sql.run('DELETE FROM inmuebles WHERE rc = ?', [rc]);
    const s = `INSERT OR REPLACE INTO inmuebles (rc20, rc, direccion, planta, puerta, superficie_m2, uso, anio, consultado_en)
      VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`;
    for (const i of filas) {
      sql.run(s, [i.rc20, i.rc, i.direccion, i.planta, i.puerta, i.superficieM2, i.uso, i.anio, i.consultadoEn]);
    }
  });
}

export function registrarTesela(sql: Sql, t: TeselaCargada): void {
  sql.run(`INSERT OR REPLACE INTO teselas (id, capa, minx, miny, maxx, maxy, epsg, cargada_en, n_registros)
    VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    [t.id, t.capa, t.minx, t.miny, t.maxx, t.maxy, t.epsg, t.cargadaEn, t.nRegistros]);
}

/** Ids de teselas de una capa cargadas hace menos de `dias`. */
export function teselasVigentes(sql: Sql, capa: TeselaCargada['capa'], ahora: number, dias: number): Set<string> {
  const limite = ahora - dias * 86_400_000;
  const filas = sql.all<{ id: string }>('SELECT id FROM teselas WHERE capa = ? AND cargada_en >= ?', [capa, limite]);
  return new Set(filas.map((f) => f.id));
}

export function listarTeselas(sql: Sql): TeselaCargada[] {
  return sql.all<Fila>('SELECT * FROM teselas ORDER BY cargada_en DESC').map((f) => ({
    id: String(f.id), capa: String(f.capa) as TeselaCargada['capa'],
    minx: num(f.minx) ?? 0, miny: num(f.miny) ?? 0, maxx: num(f.maxx) ?? 0, maxy: num(f.maxy) ?? 0,
    epsg: num(f.epsg) ?? 0, cargadaEn: num(f.cargada_en) ?? 0, nRegistros: num(f.n_registros) ?? 0,
  }));
}

/** Borra las teselas indicadas con sus parcelas, edificios y partes. */
export function borrarTeselas(sql: Sql, ids: string[]): void {
  sql.transaction(() => {
    for (const id of ids) {
      sql.run(`DELETE FROM partes WHERE rc IN (SELECT rc FROM parcelas WHERE tesela = ?)`, [id]);
      sql.run(`DELETE FROM edificios WHERE rc IN (SELECT rc FROM parcelas WHERE tesela = ?)`, [id]);
      sql.run(`DELETE FROM inmuebles WHERE rc IN (SELECT rc FROM parcelas WHERE tesela = ?)`, [id]);
      sql.run(`DELETE FROM parcelas WHERE tesela = ?`, [id]);
      sql.run(`DELETE FROM hidrografia WHERE tesela = ?`, [id]);
      sql.run(`DELETE FROM teselas WHERE id = ?`, [id]);
    }
  });
}

export interface Resumen {
  parcelas: number;
  edificios: number;
  partes: number;
  inmuebles: number;
  teselas: number;
  candidatos: number;
  cursosAgua: number;
  bbox: Bbox | null;
  epsg: number | null;
}

export function resumen(sql: Sql): Resumen {
  const c = (t: string) => num(sql.all<{ n: number }>(`SELECT COUNT(*) AS n FROM ${t}`)[0]?.n) ?? 0;
  const ext = sql.all<Fila>('SELECT MIN(minx) a, MIN(miny) b, MAX(maxx) c, MAX(maxy) d, MIN(epsg) e FROM parcelas')[0];
  const bbox: Bbox | null = ext && ext.a !== null ? [num(ext.a)!, num(ext.b)!, num(ext.c)!, num(ext.d)!] : null;
  return {
    parcelas: c('parcelas'), edificios: c('edificios'), partes: c('partes'), inmuebles: c('inmuebles'),
    teselas: c('teselas'), candidatos: c('candidatos'), cursosAgua: c('hidrografia'), bbox, epsg: ext ? num(ext.e) : null,
  };
}

// ---------- Hidrografia (cursos de agua del IGN) ----------

export function insertarCursosAgua(sql: Sql, filas: CursoAguaFila[]): void {
  const s = `INSERT OR REPLACE INTO hidrografia
    (id, nombre, tipo, persistencia, orden, ancho_max_m, longitud_m, geom, epsg, minx, miny, maxx, maxy, tesela, cargada_en)
    VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`;
  sql.transaction(() => {
    for (const c of filas) {
      sql.run(s, [c.id, c.nombre, c.tipo, c.persistencia, c.orden, c.anchoMaxM, c.longitudM, c.geom, c.epsg,
        c.minx, c.miny, c.maxx, c.maxy, c.tesela, c.cargadaEn]);
    }
  });
}

export function cursosAguaEnBbox(sql: Sql, bbox: Bbox, limite = 20_000): CursoAguaFila[] {
  return sql.all<Fila>(
    `SELECT * FROM hidrografia WHERE maxx >= ? AND minx <= ? AND maxy >= ? AND miny <= ? LIMIT ?`,
    [bbox[0], bbox[2], bbox[1], bbox[3], limite]).map(aCursoAgua);
}

/**
 * Recalcula `dist_agua_m` / `agua_nombre` / `agua_id` de las parcelas de la caja con los cursos de
 * agua cargados (los de la caja ampliada en `distMax`). Devuelve cuantas parcelas se actualizaron.
 * Se hace en la ingesta y no en la consulta (requisitos §6.2), asi la busqueda es SQL corriente.
 */
export function actualizarDistanciasAgua(sql: Sql, bbox: Bbox, distMax: number): number {
  const ampliada: Bbox = [bbox[0] - distMax, bbox[1] - distMax, bbox[2] + distMax, bbox[3] + distMax];
  const cursos = cursosAguaEnBbox(sql, ampliada, 100_000);
  const indice = new IndiceSegmentos(250);
  const nombres = new Map<string, string | null>();
  for (const c of cursos) {
    nombres.set(c.id, c.nombre);
    const g = JSON.parse(c.geom) as { coordinates: Punto[][] };
    for (const linea of g.coordinates) indice.anadir(c.id, linea);
  }
  const parcelas = sql.all<Fila>(
    'SELECT rc, geom FROM parcelas WHERE maxx >= ? AND minx <= ? AND maxy >= ? AND miny <= ?',
    [bbox[0], bbox[2], bbox[1], bbox[3]]);
  let n = 0;
  sql.transaction(() => {
    for (const p of parcelas) {
      const mp = (JSON.parse(String(p.geom)) as { coordinates: MultiPoligono }).coordinates;
      const r = indice.tamano ? indice.distanciaMultiPoligono(mp, distMax) : null;
      sql.run('UPDATE parcelas SET dist_agua_m = ?, agua_nombre = ?, agua_id = ? WHERE rc = ?',
        [r ? Math.round(r.dist * 10) / 10 : SIN_AGUA_CERCA_M, r ? nombres.get(r.id) ?? null : null, r ? r.id : null, String(p.rc)]);
      n++;
    }
  });
  return n;
}

// ---------- Busqueda ----------

export interface Rango {
  min?: number;
  max?: number;
}

export type ClaveObjetivo = 'areaParcela' | 'supConstruida' | 'huella' | 'superficieConstruccion' | 'plantas' | 'anio' | 'viviendas';

export interface Criterios {
  areaParcela?: Rango;
  supConstruida?: Rango;
  huella?: Rango;
  /** Construccion "de X m2" sin saber si es huella o construida: cumple si lo hace cualquiera de las dos. */
  superficieConstruccion?: Rango;
  plantas?: Rango;
  anio?: Rango;
  viviendas?: Rango;
  nEdificios?: Rango;
  nPartes?: Rango;
  usos?: string[];
  estados?: string[];
  conEdificio?: boolean;
  bbox?: Bbox;
  centro?: Punto;
  radioM?: number;
  /** Solo parcelas cuyo centroide cae dentro del poligono (p. ej. el termino municipal), en el huso. */
  poligono?: MultiPoligono;
  /** Distancia maxima al curso de agua mas cercano (m), calculada en la ingesta. */
  distAguaMax?: number;
  /** El nombre del curso de agua mas cercano contiene este texto (sin distinguir acentos ni mayusculas). */
  nombreAgua?: string;
  /** Valores "ideales" para puntuar el ajuste (cuanto se desvia cada candidato). */
  objetivos?: Partial<Record<ClaveObjetivo, number>>;
  limite?: number;
}

export interface Resultado {
  rc: string;
  etiqueta: string | null;
  areaM2: number | null;
  cx: number;
  cy: number;
  uso: string | null;
  estado: string | null;
  anio: number | null;
  plantas: number | null;
  viviendas: number | null;
  supConstruidaM2: number | null;
  huellaM2: number | null;
  nEdificios: number;
  nPartes: number;
  fotoUrl: string | null;
  /** Suma de desviaciones relativas respecto a los objetivos (0 = exacto). null si no hay objetivos. */
  ajuste: number | null;
  distanciaM: number | null;
  distAguaM: number | null;
  aguaNombre: string | null;
  esCandidato: boolean;
}

/** Minusculas y sin acentos, para comparar nombres de rios con lo que escribe el usuario. */
export function normalizarTexto(t: string): string {
  return t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

function rango(col: string, r: Rango | undefined, where: string[], params: Valor[]): void {
  if (!r) return;
  if (r.min !== undefined) { where.push(`${col} >= ?`); params.push(r.min); }
  if (r.max !== undefined) { where.push(`${col} <= ?`); params.push(r.max); }
}

/** Construye la consulta SQL y sus parametros (expuesto para la consola SQL y los tests). */
export function sqlDeCriterios(c: Criterios): { sql: string; params: Valor[] } {
  const where: string[] = [];
  const params: Valor[] = [];
  rango('p.area_m2', c.areaParcela, where, params);
  rango('e.sup_construida_m2', c.supConstruida, where, params);
  rango('e.huella_m2', c.huella, where, params);
  rango('e.plantas', c.plantas, where, params);
  rango('e.anio', c.anio, where, params);
  rango('e.viviendas', c.viviendas, where, params);
  rango('p.n_edificios', c.nEdificios, where, params);
  rango('p.n_partes', c.nPartes, where, params);
  if (c.superficieConstruccion) {
    const r = c.superficieConstruccion;
    const sub: string[] = [];
    for (const col of ['e.huella_m2', 'e.sup_construida_m2']) {
      const partes: string[] = [];
      if (r.min !== undefined) { partes.push(`${col} >= ?`); params.push(r.min); }
      if (r.max !== undefined) { partes.push(`${col} <= ?`); params.push(r.max); }
      if (partes.length) sub.push(`(${partes.join(' AND ')})`);
    }
    if (sub.length) where.push(`(${sub.join(' OR ')})`);
  }
  if (c.usos && c.usos.length) {
    where.push(`e.uso IN (${c.usos.map(() => '?').join(',')})`);
    params.push(...c.usos);
  }
  if (c.estados && c.estados.length) {
    where.push(`e.estado IN (${c.estados.map(() => '?').join(',')})`);
    params.push(...c.estados);
  }
  if (c.conEdificio === true) where.push('e.rc IS NOT NULL');
  if (c.conEdificio === false) where.push('e.rc IS NULL');
  if (c.bbox) {
    where.push('p.maxx >= ? AND p.minx <= ? AND p.maxy >= ? AND p.miny <= ?');
    params.push(c.bbox[0], c.bbox[2], c.bbox[1], c.bbox[3]);
  }
  if (c.centro && c.radioM) {
    where.push('p.cx BETWEEN ? AND ? AND p.cy BETWEEN ? AND ?');
    params.push(c.centro[0] - c.radioM, c.centro[0] + c.radioM, c.centro[1] - c.radioM, c.centro[1] + c.radioM);
  }
  if (c.poligono && c.poligono.length) {
    const b = bboxMultiPoligono(c.poligono);
    where.push('p.cx BETWEEN ? AND ? AND p.cy BETWEEN ? AND ?');
    params.push(b[0], b[2], b[1], b[3]);
  }
  if (c.distAguaMax !== undefined) { where.push('p.dist_agua_m <= ?'); params.push(c.distAguaMax); }
  if (c.nombreAgua && c.nombreAgua.trim()) where.push('p.agua_nombre IS NOT NULL');
  const limite = Math.max(1, Math.min(c.limite ?? 5000, 50_000));
  const sql = `SELECT p.rc, p.etiqueta, p.area_m2, p.cx, p.cy, p.n_edificios, p.n_partes,
      e.uso, e.estado, e.anio, e.plantas, e.viviendas, e.sup_construida_m2, e.huella_m2, e.foto_url,
      p.dist_agua_m, p.agua_nombre,
      (c.rc IS NOT NULL) AS es_candidato
    FROM parcelas p
    LEFT JOIN edificios e ON e.rc = p.rc
    LEFT JOIN candidatos c ON c.rc = p.rc
    ${where.length ? 'WHERE ' + where.join(' AND ') : ''}
    LIMIT ${limite}`;
  return { sql, params };
}

function desviacion(valor: number | null, objetivo: number): number | null {
  if (valor === null) return null;
  const escala = Math.max(Math.abs(objetivo), 1);
  return Math.abs(valor - objetivo) / escala;
}

export function buscar(sql: Sql, c: Criterios): Resultado[] {
  const { sql: s, params } = sqlDeCriterios(c);
  const filas = sql.all<Fila>(s, params);
  const resultados: Resultado[] = [];
  const nombreAgua = c.nombreAgua && c.nombreAgua.trim() ? normalizarTexto(c.nombreAgua) : null;
  for (const f of filas) {
    const r: Resultado = {
      rc: String(f.rc), etiqueta: str(f.etiqueta), areaM2: num(f.area_m2), cx: num(f.cx) ?? 0, cy: num(f.cy) ?? 0,
      uso: str(f.uso), estado: str(f.estado), anio: num(f.anio), plantas: num(f.plantas), viviendas: num(f.viviendas),
      supConstruidaM2: num(f.sup_construida_m2), huellaM2: num(f.huella_m2),
      nEdificios: num(f.n_edificios) ?? 0, nPartes: num(f.n_partes) ?? 0, fotoUrl: str(f.foto_url),
      ajuste: null, distanciaM: null, distAguaM: num(f.dist_agua_m), aguaNombre: str(f.agua_nombre),
      esCandidato: Number(f.es_candidato) === 1,
    };
    if (c.centro) {
      r.distanciaM = Math.hypot(r.cx - c.centro[0], r.cy - c.centro[1]);
      if (c.radioM && r.distanciaM > c.radioM) continue;
    }
    if (c.poligono && c.poligono.length && !puntoEnMultiPoligono([r.cx, r.cy], c.poligono)) continue;
    if (nombreAgua && (!r.aguaNombre || !normalizarTexto(r.aguaNombre).includes(nombreAgua))) continue;
    if (c.objetivos) {
      let suma = 0, n = 0;
      const valores: Record<ClaveObjetivo, number | null> = {
        areaParcela: r.areaM2, supConstruida: r.supConstruidaM2, huella: r.huellaM2, superficieConstruccion: null,
        plantas: r.plantas, anio: r.anio, viviendas: r.viviendas,
      };
      for (const [k, objetivo] of Object.entries(c.objetivos) as [ClaveObjetivo, number][]) {
        let d: number | null;
        if (k === 'superficieConstruccion') {
          // "Construccion de X m2" sin saber si es huella o construida: cuenta la que mejor encaje.
          const a = desviacion(r.huellaM2, objetivo), b = desviacion(r.supConstruidaM2, objetivo);
          d = a === null ? b : b === null ? a : Math.min(a, b);
        } else {
          d = desviacion(valores[k], objetivo);
        }
        if (d !== null) { suma += d; n++; } else { suma += 1; n++; }
      }
      r.ajuste = n ? suma : null;
    }
    resultados.push(r);
  }
  if (c.objetivos) resultados.sort((a, b) => (a.ajuste ?? 9e9) - (b.ajuste ?? 9e9));
  else if (c.centro) resultados.sort((a, b) => (a.distanciaM ?? 0) - (b.distanciaM ?? 0));
  else if (c.distAguaMax !== undefined || nombreAgua) resultados.sort((a, b) => (a.distAguaM ?? 9e9) - (b.distAguaM ?? 9e9));
  else resultados.sort((a, b) => a.rc.localeCompare(b.rc));
  return resultados;
}

// ---------- Ficha ----------

export interface Ficha {
  parcela: Parcela;
  edificio: Edificio | null;
  partes: Parte[];
  inmuebles: Inmueble[];
  candidato: Candidato | null;
}

export function ficha(sql: Sql, rc: string): Ficha | null {
  const p = sql.all<Fila>('SELECT * FROM parcelas WHERE rc = ?', [rc])[0];
  if (!p) return null;
  const e = sql.all<Fila>('SELECT * FROM edificios WHERE rc = ?', [rc])[0];
  return {
    parcela: aParcela(p),
    edificio: e ? aEdificio(e) : null,
    partes: sql.all<Fila>('SELECT * FROM partes WHERE rc = ? ORDER BY id', [rc]).map(aParte),
    inmuebles: sql.all<Fila>('SELECT * FROM inmuebles WHERE rc = ? ORDER BY planta, puerta', [rc]).map(aInmueble),
    candidato: candidato(sql, rc),
  };
}

export function parcela(sql: Sql, rc: string): Parcela | null {
  const p = sql.all<Fila>('SELECT * FROM parcelas WHERE rc = ?', [rc])[0];
  return p ? aParcela(p) : null;
}

/** Parcela cuyo centroide esta mas cerca del punto dentro de un radio pequeno (para el clic). */
export function parcelasCerca(sql: Sql, punto: Punto, radioM: number): Parcela[] {
  return sql.all<Fila>(
    'SELECT * FROM parcelas WHERE maxx >= ? AND minx <= ? AND maxy >= ? AND miny <= ?',
    [punto[0] - radioM, punto[0] + radioM, punto[1] - radioM, punto[1] + radioM]).map(aParcela);
}

// ---------- Geometria para el mapa ----------

export interface FilaGeo {
  rc: string;
  geom: string;
  epsg: number;
  areaM2: number | null;
  uso: string | null;
  anio: number | null;
  plantas: number | null;
  supConstruidaM2: number | null;
  huellaM2: number | null;
  viviendas: number | null;
  esCandidato: boolean;
}

export function parcelasEnBbox(sql: Sql, bbox: Bbox, limite = 30_000): FilaGeo[] {
  return sql.all<Fila>(
    `SELECT p.rc, p.geom, p.epsg, p.area_m2, e.uso, e.anio, e.plantas, e.sup_construida_m2, e.huella_m2, e.viviendas,
       (c.rc IS NOT NULL) AS es_candidato
     FROM parcelas p LEFT JOIN edificios e ON e.rc = p.rc LEFT JOIN candidatos c ON c.rc = p.rc
     WHERE p.maxx >= ? AND p.minx <= ? AND p.maxy >= ? AND p.miny <= ? LIMIT ?`,
    [bbox[0], bbox[2], bbox[1], bbox[3], limite]).map((f) => ({
    rc: String(f.rc), geom: String(f.geom), epsg: num(f.epsg) ?? 0, areaM2: num(f.area_m2), uso: str(f.uso),
    anio: num(f.anio), plantas: num(f.plantas), supConstruidaM2: num(f.sup_construida_m2), huellaM2: num(f.huella_m2),
    viviendas: num(f.viviendas), esCandidato: Number(f.es_candidato) === 1,
  }));
}

export interface FilaGeoParte {
  id: string;
  rc: string;
  geom: string;
  plantasSobre: number | null;
  plantasBajo: number | null;
  uso: string | null;
  anio: number | null;
  esCandidato: boolean;
  aleroM: number | null;
  cumbreraM: number | null;
}

export function partesEnBbox(sql: Sql, bbox: Bbox, limite = 60_000): FilaGeoParte[] {
  return sql.all<Fila>(
    `SELECT x.id, x.rc, x.geom, x.plantas_sobre, x.plantas_bajo, e.uso, e.anio, e.alero_m, e.cumbrera_m,
       (c.rc IS NOT NULL) AS es_candidato
     FROM partes x JOIN parcelas p ON p.rc = x.rc LEFT JOIN edificios e ON e.rc = x.rc LEFT JOIN candidatos c ON c.rc = x.rc
     WHERE p.maxx >= ? AND p.minx <= ? AND p.maxy >= ? AND p.miny <= ? LIMIT ?`,
    [bbox[0], bbox[2], bbox[1], bbox[3], limite]).map((f) => ({
    id: String(f.id), rc: String(f.rc), geom: String(f.geom), plantasSobre: num(f.plantas_sobre),
    plantasBajo: num(f.plantas_bajo), uso: str(f.uso), anio: num(f.anio), esCandidato: Number(f.es_candidato) === 1,
    aleroM: num(f.alero_m), cumbreraM: num(f.cumbrera_m),
  }));
}

export interface FilaGeoEdificio {
  rc: string;
  geom: string;
  huellaCorregida: string | null;
  plantas: number | null;
  aleroM: number | null;
  cumbreraM: number | null;
}

export function edificiosEnBbox(sql: Sql, bbox: Bbox, limite = 30_000): FilaGeoEdificio[] {
  return sql.all<Fila>(
    `SELECT e.rc, e.geom, e.huella_corregida, e.plantas, e.alero_m, e.cumbrera_m FROM edificios e JOIN parcelas p ON p.rc = e.rc
     WHERE p.maxx >= ? AND p.minx <= ? AND p.maxy >= ? AND p.miny <= ? LIMIT ?`,
    [bbox[0], bbox[2], bbox[1], bbox[3], limite]).map((f) => ({
    rc: String(f.rc), geom: String(f.geom), huellaCorregida: str(f.huella_corregida),
    plantas: num(f.plantas), aleroM: num(f.alero_m), cumbreraM: num(f.cumbrera_m),
  }));
}

export function parcelasEtiquetadasEnBbox(sql: Sql, bbox: Bbox, limite = 30_000): { rc: string; etiqueta: string | null; geom: string; epsg: number }[] {
  return sql.all<Fila>(
    `SELECT rc, etiqueta, geom, epsg FROM parcelas WHERE maxx >= ? AND minx <= ? AND maxy >= ? AND miny <= ? LIMIT ?`,
    [bbox[0], bbox[2], bbox[1], bbox[3], limite]).map((f) => ({ rc: String(f.rc), etiqueta: str(f.etiqueta), geom: String(f.geom), epsg: num(f.epsg) ?? 0 }));
}

/** Referencias de una tesela que aun no tienen partes cargadas (para la carga bajo demanda del 3D). */
export function rcsSinPartes(sql: Sql, bbox: Bbox): string[] {
  return sql.all<{ rc: string }>(
    `SELECT p.rc FROM parcelas p WHERE p.n_edificios > 0 AND p.n_partes = 0
       AND p.maxx >= ? AND p.minx <= ? AND p.maxy >= ? AND p.miny <= ?`,
    [bbox[0], bbox[2], bbox[1], bbox[3]]).map((f) => String(f.rc));
}

// ---------- Candidatos y busquedas ----------

export function candidato(sql: Sql, rc: string): Candidato | null {
  const f = sql.all<Fila>('SELECT * FROM candidatos WHERE rc = ?', [rc])[0];
  return f ? aCandidato(f) : null;
}

export function guardarCandidato(sql: Sql, c: Candidato): void {
  sql.run(`INSERT OR REPLACE INTO candidatos (rc, anuncio_url, precio, etiqueta, notas, creado) VALUES (?, ?, ?, ?, ?, ?)`,
    [c.rc, c.anuncioUrl, c.precio, c.etiqueta, c.notas, c.creado]);
}

export function borrarCandidato(sql: Sql, rc: string): void {
  sql.run('DELETE FROM candidatos WHERE rc = ?', [rc]);
}

export interface CandidatoCompleto extends Candidato {
  parcela: Parcela | null;
  edificio: Edificio | null;
  inmuebles: Inmueble[];
}

export function listarCandidatos(sql: Sql): CandidatoCompleto[] {
  return sql.all<Fila>('SELECT * FROM candidatos ORDER BY creado DESC').map((f) => {
    const c = aCandidato(f);
    const p = sql.all<Fila>('SELECT * FROM parcelas WHERE rc = ?', [c.rc])[0];
    const e = sql.all<Fila>('SELECT * FROM edificios WHERE rc = ?', [c.rc])[0];
    const inm = sql.all<Fila>('SELECT * FROM inmuebles WHERE rc = ? ORDER BY planta, puerta', [c.rc]).map(aInmueble);
    return { ...c, parcela: p ? aParcela(p) : null, edificio: e ? aEdificio(e) : null, inmuebles: inm };
  });
}

export function guardarBusqueda(sql: Sql, b: BusquedaGuardada): void {
  sql.run('INSERT OR REPLACE INTO busquedas (id, nombre, criterios, creada) VALUES (?, ?, ?, ?)', [b.id, b.nombre, b.criterios, b.creada]);
}

export function borrarBusqueda(sql: Sql, id: string): void {
  sql.run('DELETE FROM busquedas WHERE id = ?', [id]);
}

export function listarBusquedas(sql: Sql): BusquedaGuardada[] {
  return sql.all<Fila>('SELECT * FROM busquedas ORDER BY creada DESC').map((f) => ({
    id: String(f.id), nombre: String(f.nombre), criterios: String(f.criterios), creada: num(f.creada) ?? 0,
  }));
}

// ---------- Exportacion en bruto ----------

export type TablaExportable = 'parcelas' | 'edificios' | 'partes' | 'inmuebles' | 'candidatos' | 'hidrografia';

export function exportarTabla(sql: Sql, tabla: TablaExportable): Fila[] {
  return sql.all<Fila>(`SELECT * FROM ${tabla}`);
}

/** Consulta libre de solo lectura para la consola SQL: rechaza sentencias que modifican. */
export function consultaLibre(sql: Sql, texto: string, limite = 1000): Fila[] {
  const t = texto.trim().replace(/;+$/, '');
  if (!/^\s*(select|with|pragma table_info|explain)\b/i.test(t)) {
    throw new Error('La consola solo admite consultas SELECT.');
  }
  if (/\b(insert|update|delete|drop|alter|create|replace|attach|vacuum)\b/i.test(t)) {
    throw new Error('La consola solo admite consultas de lectura.');
  }
  return sql.all<Fila>(`SELECT * FROM (${t}) LIMIT ${Math.max(1, Math.min(limite, 10_000))}`);
}

// ---------- Mapeo de filas ----------

function num(v: Valor | undefined): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function str(v: Valor | undefined): string | null {
  return v === null || v === undefined ? null : String(v);
}

function aParcela(f: Fila): Parcela {
  return {
    rc: String(f.rc), etiqueta: str(f.etiqueta), areaM2: num(f.area_m2), cx: num(f.cx) ?? 0, cy: num(f.cy) ?? 0,
    minx: num(f.minx) ?? 0, miny: num(f.miny) ?? 0, maxx: num(f.maxx) ?? 0, maxy: num(f.maxy) ?? 0,
    nEdificios: num(f.n_edificios) ?? 0, nPartes: num(f.n_partes) ?? 0, geom: String(f.geom), epsg: num(f.epsg) ?? 0,
    tesela: String(f.tesela ?? ''), actualizada: num(f.actualizada) ?? 0,
    distAguaM: num(f.dist_agua_m), aguaNombre: str(f.agua_nombre), aguaId: str(f.agua_id),
  };
}

function aCursoAgua(f: Fila): CursoAguaFila {
  return {
    id: String(f.id), nombre: str(f.nombre), tipo: str(f.tipo), persistencia: str(f.persistencia), orden: num(f.orden),
    anchoMaxM: num(f.ancho_max_m), longitudM: num(f.longitud_m), geom: String(f.geom), epsg: num(f.epsg) ?? 0,
    minx: num(f.minx) ?? 0, miny: num(f.miny) ?? 0, maxx: num(f.maxx) ?? 0, maxy: num(f.maxy) ?? 0,
    tesela: String(f.tesela ?? ''), cargadaEn: num(f.cargada_en) ?? 0,
  };
}

function aEdificio(f: Fila): Edificio {
  return {
    rc: String(f.rc), uso: str(f.uso), estado: str(f.estado), anio: num(f.anio), plantas: num(f.plantas),
    unidades: num(f.unidades), viviendas: num(f.viviendas), supConstruidaM2: num(f.sup_construida_m2),
    huellaM2: num(f.huella_m2), fotoUrl: str(f.foto_url), geom: String(f.geom), aleroM: num(f.alero_m),
    cumbreraM: num(f.cumbrera_m), huellaCorregida: str(f.huella_corregida), desviacionM: num(f.desviacion_m),
  };
}

function aParte(f: Fila): Parte {
  return {
    id: String(f.id), rc: String(f.rc), plantasSobre: num(f.plantas_sobre), plantasBajo: num(f.plantas_bajo),
    alturaBajoM: num(f.altura_bajo_m), estado: str(f.estado), huellaM2: num(f.huella_m2), geom: String(f.geom),
  };
}

function aInmueble(f: Fila): Inmueble {
  return {
    rc20: String(f.rc20), rc: String(f.rc), direccion: str(f.direccion), planta: str(f.planta), puerta: str(f.puerta),
    superficieM2: num(f.superficie_m2), uso: str(f.uso), anio: num(f.anio), consultadoEn: num(f.consultado_en) ?? 0,
  };
}

function aCandidato(f: Fila): Candidato {
  return {
    rc: String(f.rc), anuncioUrl: str(f.anuncio_url), precio: num(f.precio), etiqueta: str(f.etiqueta),
    notas: str(f.notas), creado: num(f.creado) ?? 0,
  };
}
